import type { Maybe } from '@snowit/icetypes';
import type { OrderItemProfileNode, PurchasedItemProfileNode } from '@snowit/models';
import type { Model } from '@snowit/types/firestore';
import type { FiltersSchema } from '@snowit/types/shared';
import type { UserModel, UserProfileModel } from '@snowit/types/users';

import snowment from '@snowit/utils/snowmentv2';

import { mapItemProfileToUserProfile, mapOrderItemProfile } from './mappers';

export const getProfiles = (
  profile?: Maybe<PurchasedItemProfileNode>,
  profiles?: Maybe<Record<string, PurchasedItemProfileNode>>,
): UserProfileModel[] => {
  if (profiles) {
    const array = Object.values(profiles);
    if (array.length) {
      return array.map(mapItemProfileToUserProfile);
    }
  }
  if (profile) return [mapItemProfileToUserProfile(profile)];
  return [];
};

export const getOrderProfiles = (
  profiles?: Maybe<Record<string, OrderItemProfileNode>>,
): UserProfileModel[] => {
  if (!profiles) return [];
  const array = Object.values(profiles);
  if (!array.length) return [];
  return array.map(mapOrderItemProfile);
};

export const uniqueModel = <T extends Record<string, any>>(
  memo: T[],
  model: T,
  _index?: number,
  _self?: T[],
) => {
  const found = memo.find((it) => it.uid === model.uid);
  if (found) return memo;
  return [...memo, model];
};

export const sortModel = (a: Model<any>, b: Model<any>): number => {
  return (b?.createdAt?.seconds ?? 0) - (a?.createdAt?.seconds ?? 0);
};

export const ObjectKeysOnly = (obj: Record<string, any>, keys: string[]) => {
  const objKeys: string[] = Object.entries(obj)
    .filter(([, value]) => typeof value !== 'undefined')
    .map(([key]) => key);
  return objKeys.length === keys.length && keys.every((k) => objKeys.includes(k));
};

export const getUserName = (input?: Maybe<UserModel>): string => {
  if (!input) return '';
  if (input.displayName) return input.displayName;
  return [input.name, input.surname].filter(Boolean).join(' ');
};

export const getPPUSeasons = (yearsFuture = 0) => {
  const today = snowment();
  const FIRST_YEAR = 2024;
  const DATE = '07-01';

  const ALL_YEARS = Array.from({ length: today.year + 1 - FIRST_YEAR + yearsFuture })
    .map((_, i) => FIRST_YEAR + i)
    .filter((year) => {
      if (yearsFuture > 0) return true;
      return today.isSameOrAfter(`${year}-${DATE}`, 'day');
    });
  const CURRENT = ALL_YEARS[ALL_YEARS.length - 1];

  return {
    seasons: ALL_YEARS.map((year) => `${year}-${year + 1}`),
    current: `${CURRENT}-${CURRENT + 1}`,
  };
};

export const schemaToQuery = <T extends any>(schema: FiltersSchema<T>[], limit?: number) => {
  return schema
    .filter((entry) => {
      if (typeof entry.when === 'function') {
        return entry.when();
      }
      return entry.when;
    })
    .filter((entry) => {
      if (typeof entry.value === 'function') {
        return entry.value();
      }
      return !!entry.value;
    })
    .map((entry) => {
      const value = typeof entry.value === 'function' ? entry.value() : entry.value;
      if (Array.isArray(value)) return value;
      return [];
    })
    .reduce((memo, value) => [...memo, ...value], limit ? [['limit', limit]] : []);
};
